import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { b as EGDSImage } from '../image-link/index-chunk.esm.js';
import * as React from 'react';
import { c as classNames, S as ScriptLoader } from '../theme-provider/index-chunk.esm.js';
import styles from '../styles/uitk-egds-video.css';
import { a as analyticsTracker } from '../analytics-tracker/index-chunk.esm.js';
import { useLocalization } from '@shared-ui/localization-context';
import { E as EGDSButtonSize } from '../paging-button/index-chunk.esm.js';
import { E as EGDSOverlayButton } from '../overlay-button/index-chunk.esm.js';
import { a as EGDSIcon, E as EGDSIconSize } from '../icon/index-chunk.esm.js';
import { d as EGDSLayoutPosition } from '../layout-position/index-chunk.esm.js';
import { E as EGDSScrim, a as EGDSScrimType } from '../scrim/index-chunk.esm.js';
import { E as EGDSLoader } from '../page-loader/index-chunk.esm.js';

const getClassNameList$3 = classNames.bind(styles);
const EGDSBasicVideo = (props) => {
    const { analytics, autoplay, className, crossOrigin, muted, pauseOnOtherPlay = true, playsInline = true, poster, preload, src, tracks, videoRef, } = props;
    const wrapperVideoClassNames = getClassNameList$3('uitk-video-basic', className);
    const onPlay = (e) => {
        // When starting to play one video, pause all others
        const videoElements = document.querySelectorAll('video.pause-on-other-play');
        for (const video of videoElements) {
            if (video !== e.target) {
                video.pause();
            }
        }
        analyticsTracker(analytics, 'playVideo', '');
    };
    /* eslint-disable jsx-a11y/media-has-caption */
    return (jsx("div", { className: wrapperVideoClassNames, children: jsx("video", { onPlay: onPlay, onPause: () => analyticsTracker(analytics, 'pauseVideo', ''), autoPlay: autoplay, controls: true, crossOrigin: crossOrigin, muted: muted, playsInline: playsInline, poster: poster, preload: preload, ref: videoRef, src: src, className: pauseOnOtherPlay ? 'pause-on-other-play' : '', children: tracks &&
                // Default caption type to 'captions' if kind is not predefined.
                tracks.map((trackProps, i) => jsx("track", { kind: "captions", ...trackProps }, i)) }) }));
};

var PlayerState;
(function (PlayerState) {
    PlayerState[PlayerState["UNSTARTED"] = -1] = "UNSTARTED";
    PlayerState[PlayerState["ENDED"] = 0] = "ENDED";
    PlayerState[PlayerState["PLAYING"] = 1] = "PLAYING";
    PlayerState[PlayerState["PAUSED"] = 2] = "PAUSED";
    PlayerState[PlayerState["BUFFERING"] = 3] = "BUFFERING";
    PlayerState[PlayerState["CUED"] = 5] = "CUED";
})(PlayerState || (PlayerState = {}));
var VideoType;
(function (VideoType) {
    VideoType["EMBED"] = "embed";
    VideoType["BASIC"] = "basic";
})(VideoType || (VideoType = {}));

/* istanbul ignore file */
const useVideoHook = (analytics) => {
    const [playerError, setPlayerError] = React.useState({ status: false, data: 0 });
    const [isPlayerReady, setIsPlayerReady] = React.useState(false);
    const [playerState, setPlayerState] = React.useState(PlayerState.UNSTARTED);
    const [player, setPlayer] = React.useState();
    const onPlayerError = (event) => {
        setPlayerError({ status: true, data: event.data });
    };
    const onPlayerReady = (event) => {
        setIsPlayerReady(true);
        setPlayerState(event.target.getPlayerState());
    };
    const onPlayerStateChange = (event) => {
        setPlayerState(event.data);
    };
    const onClickPlay = () => {
        if (!player)
            console.warn('Play noop; no YT player found');
        player?.playVideo();
        if (analytics) {
            analyticsTracker(analytics, 'playVideo', '');
        }
    };
    const onClickPause = () => {
        if (!player)
            console.warn('Pause noop; No YT player found');
        player?.pauseVideo();
        if (analytics) {
            analyticsTracker(analytics, 'pauseVideo', '');
        }
    };
    return {
        playerError,
        onPlayerError,
        isPlayerReady,
        onPlayerReady,
        playerState,
        onPlayerStateChange,
        player,
        setPlayer,
        onClickPlay,
        onClickPause,
    };
};

const getClassNameList$2 = classNames.bind(styles);
const EGDSEmbedVideo = (props) => {
    const { alt, analytics, className, isDialog, src, buttonText } = props;
    const { playerError, onPlayerError, isPlayerReady, onPlayerReady, playerState, onPlayerStateChange, player, setPlayer, onClickPlay, onClickPause, } = useVideoHook(analytics);
    const videoId = getVideoId(src);
    const [playerVideoId, setPlayerVideoId] = React.useState(videoId);
    const uniqueIdRef = React.useRef(playerVideoId + Math.random().toString(36)); // Can replace with useId in React 18
    const wrapperVideoClassNames = getClassNameList$2('uitk-video', className);
    let playControlTriggered = false;
    /* istanbul ignore next */
    const createPlayer = () => {
        setPlayer(new window.YT.Player(`player-${videoId}${isDialog ? '-dialog' : ''}`, {
            events: {
                onError: onPlayerError,
                onReady: onPlayerReady,
                onStateChange: onPlayerStateChange,
            },
            height: '100%',
            playerVars: {
                controls: 1,
                disablekb: 1,
                enablejsapi: 1,
                iv_load_policy: 3,
                modestbranding: 1,
                rel: 0,
            },
            videoId,
            width: '100%',
        }));
    };
    React.useEffect(() => {
        if (!window.RunOnYouTubeIframeAPIReady) {
            window.RunOnYouTubeIframeAPIReady = new Map();
        }
        if (!window.onYouTubeIframeAPIReady) {
            window.onYouTubeIframeAPIReady = () => {
                // Dequeue and run all functions in the map
                while (window.RunOnYouTubeIframeAPIReady.size) {
                    window.RunOnYouTubeIframeAPIReady.forEach((fn, key) => {
                        fn();
                        window.RunOnYouTubeIframeAPIReady.delete(key);
                    });
                }
            };
        }
        // YT could be available but YT.Player isn't available yet, especially on a Slow network.
        // YT.Player is added by a different script (www-widgetapi.js) than window.YT
        /* istanbul ignore next */
        if (!window.YT) {
            ScriptLoader.getScript(`https://www.youtube.com/iframe_api`).catch(console.error);
            window.RunOnYouTubeIframeAPIReady.set(uniqueIdRef.current, createPlayer);
        }
        else if (!window.YT.Player) {
            window.RunOnYouTubeIframeAPIReady.set(uniqueIdRef.current, createPlayer);
        }
        else if (!player && !window.RunOnYouTubeIframeAPIReady.has(uniqueIdRef.current)) {
            createPlayer();
        }
        else if (player && videoId !== playerVideoId && isPlayerReady) {
            setPlayerVideoId(videoId);
            player.cueVideoById(videoId);
        }
    });
    if (typeof document === 'undefined') {
        return null;
    }
    return (jsxs("div", { className: wrapperVideoClassNames, children: [jsx(EGDSVideoLayer, { alt: alt || '', controls: {
                    pause: onClickPause,
                    play: () => {
                        if (window && !playControlTriggered) {
                            // Necessary a11y fix to prevent the Youtube Iframe player's controls from being tabbable before users trigger the EGDSVideo controls
                            const youtubeIframeElement = window.document?.getElementById(`player-${videoId}${isDialog ? '-dialog' : ''}`);
                            youtubeIframeElement?.setAttribute('tabIndex', '0');
                            playControlTriggered = true;
                        }
                        onClickPlay();
                    },
                }, buttonText: buttonText, isPlayerReady: isPlayerReady, playerError: playerError, playerState: playerState, src: src }), jsx("div", { tabIndex: -1, id: `player-${videoId}${isDialog ? '-dialog' : ''}` })] }));
};

const EGDSVideo = (props) => {
    const { videoType = VideoType.EMBED } = props;
    switch (videoType) {
        case VideoType.EMBED:
            return jsx(EGDSEmbedVideo, { ...props });
        case VideoType.BASIC:
            return jsx(EGDSBasicVideo, { ...props });
        default:
            return jsx(EGDSEmbedVideo, { ...props });
    }
};

/**
 * Get the YouTube video Id from source Url.
 *
 * @param {string} src - The original YouTube Url.
 * @returns {string} - YouTube video Id.
 */
const getVideoId = (src) => {
    if (!src) {
        return '';
    }
    if (/v=/.test(src)) {
        const splitedString = src.split('v=');
        if (/&/.test(splitedString[1])) {
            return splitedString[1].split('&')[0];
        }
        return splitedString[1];
    }
    if (/youtu.be/.test(src)) {
        const splitedString = src.split('youtu.be/');
        return splitedString[1];
    }
    return src.match(/[^/]+$/)?.[0] || '';
};
/**
 * Check if url is valid YouTube thumbnail image.
 *
 * @param {string} src - The original YouTube Url.
 * @returns {boolean} - Returns boolean depending on image size.
 */
const checkImgUrl = (url) => {
    const img = new Image();
    img.src = url;
    return img.height > 90;
};
/**
 * Get max resolution thumbnail.
 *
 * @param {string} src - The original YouTube Url.
 * @returns {string} - Returns best image resolution thumbnail link is available.
 */
const youTubeThumbnailImage = (src) => {
    if (!src) {
        return '';
    }
    const imgResolutions = ['maxresdefault.jpg', 'hqdefault.jpg', 'sddefault.jpg', 'mqdefault.jpg', 'default.jpg'];
    const videoId = getVideoId(src);
    const youTubeThumbnailLink = 'https://i1.ytimg.com/vi';
    const thumbnailUrl = imgResolutions
        // eslint-disable-next-line array-callback-return
        .map((imgResolution) => {
        const url = `${youTubeThumbnailLink}/${videoId}/${imgResolution}`;
        if (checkImgUrl(url)) {
            return url;
        }
    })
        .filter((url) => typeof url !== 'undefined');
    return thumbnailUrl[0] || '';
};
/**
 * Check if provided src is a valid YouTube Url.
 *
 * @param {string} src - url
 * @returns {boolean}
 */
const isYoutubeSource = (src) => {
    if (!src) {
        return false;
    }
    return /(www\.)youtube\./.test(src) || /youtu\.be/.test(src);
};

const getClassNameList$1 = classNames.bind(styles);
const playerStateImageVisible = [PlayerState.ENDED, PlayerState.UNSTARTED, PlayerState.CUED];
const EGDSVideoLayer = ({ alt, controls, isPlayerReady, playerError, playerState, src, buttonText, }) => {
    const coverSrc = youTubeThumbnailImage(src);
    const isPlaceholderImageVisibile = playerStateImageVisible.includes(playerState) && !playerError.status;
    return (jsxs(Fragment, { children: [isPlaceholderImageVisibile && jsx(EGDSImage, { alt: alt, blankDefault: !coverSrc, src: coverSrc }), jsx(EGDSVideoLayerLoading, { className: getClassNameList$1({ 'is-hidden': isPlayerReady }), playerState: playerState }), jsx(EGDSVideoLayerControls, { className: getClassNameList$1({
                    'is-hidden': !isPlayerReady || playerError.status,
                    'uitk-image-placeholder uitk-image-placeholder-image': !coverSrc && ![PlayerState.PLAYING, PlayerState.PAUSED].includes(playerState),
                }), buttonText: buttonText, controls: controls, playerState: playerState })] }));
};

const getClassNameList = classNames.bind(styles);
const EGDSVideoLayerControls = ({ className, controls, playerState, buttonText, }) => {
    const l10n = useLocalization();
    const playPauseControls = () => (playerState === PlayerState.PLAYING ? controls.pause : controls.play);
    const playPauseText = () => {
        if (playerState === PlayerState.PLAYING) {
            return buttonText?.pauseButton ?? l10n.formatText('video.pauseButton.label');
        }
        return buttonText?.playButton ?? l10n.formatText('video.playButton.label');
    };
    const shouldHideControls = [
        PlayerState.BUFFERING,
        PlayerState.PLAYING,
        PlayerState.PAUSED,
        PlayerState.UNSTARTED,
    ].includes(playerState);
    return (jsxs("div", { className: getClassNameList(className), children: [playerState === PlayerState.UNSTARTED ? jsx(EGDSScrim, { type: EGDSScrimType.OVERLAY }) : null, jsx("button", { type: "button", "aria-label": playPauseText(), className: getClassNameList('uitk-video-layer-controls-link', {
                    'uitk-video-hide-controls': shouldHideControls,
                }), onClick: playPauseControls(), children: jsx("span", { className: getClassNameList('uitk-video-hide-controls'), children: playPauseText() }) }), jsx(EGDSLayoutPosition, { type: "absolute", position: { top: 'center', left: 'center' }, className: getClassNameList('uitk-video-play-button-wrapper'), children: jsx(EGDSOverlayButton, { "aria-hidden": "true", tabIndex: -1, onClick: playPauseControls(), size: EGDSButtonSize.LARGE, className: getClassNameList({
                        'uitk-video-hide-controls': shouldHideControls,
                    }), children: jsx(EGDSIcon, { id: "play", name: "play_arrow", size: EGDSIconSize.LARGE, title: playPauseText() }) }) })] }));
};

const EGDSVideoLayerLoading = ({ className, playerState, }) => {
    const { formatText } = useLocalization();
    return (jsxs(Fragment, { children: [playerState === PlayerState.UNSTARTED ? jsx(EGDSScrim, { type: EGDSScrimType.OVERLAY }) : null, jsx(EGDSLayoutPosition, { className: className, type: "absolute", position: { top: 'center', left: 'center' }, children: jsx(EGDSLoader, { isDarkBackground: true, title: formatText('video.loading.title') }) })] }));
};

export { EGDSVideo as E, EGDSVideoLayer as a, EGDSVideoLayerControls as b, checkImgUrl as c, EGDSVideoLayerLoading as d, EGDSBasicVideo as e, EGDSEmbedVideo as f, getVideoId as g, isYoutubeSource as i, youTubeThumbnailImage as y };
//# sourceMappingURL=index-chunk.esm.js.map
